import FormField from '@risk-smart/themed-cloudscape-components/form-field';
import { ParentTypes as Parent_Type_Enum } from '@risksmart-app/domain/src/types/consts/parent-type';
import { capitalize } from 'lodash';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import Select from 'src/components/form/select';
import type { ObjectWithContributors } from 'src/rbac/Permission';
import { useHasPermissionQuery } from 'src/rbac/useHasPermission';

import { useIsModuleEnabled } from '@/hooks/useIsModuleEnabled';

import type { ResultType } from '../modals/types';
import {
  type ResultFamily,
  resultPermissionTypes,
} from './resultPermissionTypes';

interface Props {
  readOnly: boolean;
  onChange?: (val: ResultType) => void;
  parent?: ObjectWithContributors;
  value: ResultType;
  testId?: string;
  /** Register whose result types gate the options. */
  resultFamily: ResultFamily;
}

const TypeSelector: FC<Props> = ({
  readOnly,
  onChange,
  parent,
  value,
  testId,
  resultFamily,
}) => {
  const { t } = useTranslation('common', {
    keyPrefix: 'assessmentResults.fields',
  });
  const { t: tt } = useTranslation('taxonomy');

  const policyModuleEnabled = useIsModuleEnabled('document');
  const complianceModuleEnabled = useIsModuleEnabled('obligation');

  // Result types of the register being written — an oversight result is
  // governed by `document_oversight_result` etc., not by the first-line
  // `document_assessment_result` (COI-639).
  const permissionTypes = resultPermissionTypes[resultFamily];

  const {
    hasPermission: canCreateDocumentAssessmentResult,
    loading: canCreateDocumentLoading,
  } = useHasPermissionQuery(`insert:${permissionTypes.document}`, parent);
  const {
    hasPermission: canCreateObligationAssessmentResult,
    loading: canCreateObligationLoading,
  } = useHasPermissionQuery(`insert:${permissionTypes.obligation}`, parent);
  const {
    hasPermission: canCreateRiskAssessmentResult,
    loading: canCreateRiskLoading,
  } = useHasPermissionQuery(
    [
      `insert:${permissionTypes.riskControlled}`,
      `insert:${permissionTypes.riskUncontrolled}`,
    ],
    parent
  );

  const options = [];

  if (
    canCreateDocumentAssessmentResult &&
    !canCreateDocumentLoading &&
    policyModuleEnabled
  ) {
    options.push({
      value: Parent_Type_Enum.DocumentAssessmentResult,
      label: capitalize(tt('document_one')),
    });
  }

  if (canCreateRiskAssessmentResult && !canCreateRiskLoading) {
    options.push({
      value: Parent_Type_Enum.RiskAssessmentResult,
      label: capitalize(tt('risk_one')),
    });
  }

  if (
    canCreateObligationAssessmentResult &&
    !canCreateObligationLoading &&
    complianceModuleEnabled
  ) {
    options.push({
      value: Parent_Type_Enum.ObligationAssessmentResult,
      label: capitalize(tt('obligation_one')),
    });
  }

  return (
    <FormField label={t('Type')} data-testid={`form-field-${testId}`}>
      <Select
        disabled={readOnly || options.length === 1}
        onChange={(e) =>
          onChange?.(e.detail.selectedOption.value as ResultType)
        }
        options={options}
        selectedOption={options.find((o) => o.value === value) || null}
        placeholder={t('Type_placeholder')}
        expandToViewport
      />
    </FormField>
  );
};

export default TypeSelector;
